// Settings page — /settings.
//
// Seven tabs (CustomerDetailPage's pattern, simpler because no per-tab lazy
// fetch and no URL params):
//
//   1. General      — defaultQuoteTiers, marginWarningThreshold, defaultJobType
//   2. Defaults     — collar-shop overrides (feed, DOC, kerf, sheet, rates, times)
//   3. Vendors      — outside-service / material supplier matrix (CRUD)
//   4. Machines     — shop machines + hourly rates (CRUD)
//   5. Materials    — stock library w/ vendor linkage (CRUD)
//   6. Jemma        — capability registry rendered as toggle groups by category
//   7. Account      — signed-in email, change-password trigger, sign-out button
//
// Each tab owns its own form state + Save button with inline "Saving…" / "Saved ✓"
// feedback. We do NOT auto-save on field change — settings tend to be edited in
// batches and a per-keystroke write is wasted Firebase traffic + makes "did I
// change anything" hard to feel.
//
// Why the registry-driven Jemma tab: the Settings page should never need to
// be touched when a new capability lands. Other modules register at load
// time; this page reads from listJemmaCapabilities() and renders whatever's
// there, grouped by category.

import { useEffect, useMemo, useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  auth,
  currentFirebaseUser,
  observeAuthState,
  signOut as firebaseSignOut,
} from '@/shared/firebase';
import {
  getUserSettings,
  updateUserSettings,
  type CollarDefaultsShape,
  type UserSettings,
} from '@/shared/userSettings';
import { COLLAR_DEFAULTS } from '@/shared/collarMath';
import {
  listJemmaCapabilities,
  type JemmaCapability,
  type JemmaCapabilityCategory,
} from '@/shared/jemmaCapabilities';
import type {
  JobType,
  Machine,
  Material,
  MaterialCategory,
  MaterialForm,
  MaterialPriceSnapshot,
  Vendor,
  VendorServiceType,
} from '@/shared/crmTypes';
import {
  MATERIAL_CATEGORY_LABELS,
  MATERIAL_CATEGORY_COLORS,
  VENDOR_SERVICE_LABELS,
} from '@/shared/crmTypes';
import {
  createVendor,
  listVendors,
  updateVendor,
  softDeleteVendor,
  reorderInGroup as reorderVendorInGroup,
  type CreateVendorInput,
  type UpdateVendorInput,
} from '@/shared/vendors';
import {
  createMachine,
  listMachines,
  updateMachine,
  softDeleteMachine,
  reorderInGroup as reorderMachineInGroup,
  type CreateMachineInput,
  type UpdateMachineInput,
} from '@/shared/machines';
import {
  createMaterial,
  listMaterials,
  updateMaterial,
  softDeleteMaterial,
  reorderInGroup as reorderMaterialInGroup,
  type CreateMaterialInput,
  type UpdateMaterialInput,
} from '@/shared/materials';
import { TagInput } from '@/components/TagInput';
import { GroupedListControls } from '@/components/GroupedListControls';

// =====================================================
// Constants
// =====================================================

type Tab = 'general' | 'defaults' | 'vendors' | 'machines' | 'materials' | 'jemma' | 'account';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'defaults', label: 'Defaults' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'machines', label: 'Machines' },
  { key: 'materials', label: 'Materials' },
  { key: 'jemma', label: 'Jemma capabilities' },
  { key: 'account', label: 'Account' },
];

const JOB_TYPES: Array<{ value: JobType; label: string }> = [
  { value: 'multi_part_production', label: 'Multi-part production' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'one_off', label: 'One-off' },
  { value: 'quick_quote', label: 'Quick quote' },
  { value: 'spot_quote', label: 'Spot quote' },
  { value: 'welding_job', label: 'Welding job' },
  { value: 'machine_build', label: 'Machine build' },
];

const DEFAULT_TIERS_FALLBACK = '10 / 50 / 100';
const DEFAULT_MARGIN_WARN = 25;

/** Section labels for the Jemma tab. Order = display order. */
const CATEGORY_LABELS: Record<JemmaCapabilityCategory, string> = {
  chat: 'Chat',
  email: 'Email',
  memory: 'Memory',
  data: 'Data',
  integration: 'Integrations',
  system: 'System',
};
const CATEGORY_ORDER: JemmaCapabilityCategory[] = [
  'chat',
  'memory',
  'data',
  'email',
  'integration',
  'system',
];

// =====================================================
// Shared helpers
// =====================================================

/**
 * Parse "10 / 50 / 100" → [10, 50, 100]. Same rules as NewPartModal's
 * parseQtyTiers: split on non-digits, 1-5 positive integers, all finite.
 * Returns null on any parse failure so the caller can show a single error
 * for the whole field.
 */
function parseTiers(raw: string): number[] | null {
  const nums = raw
    .split(/[^\d]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number.parseInt(s, 10));
  if (nums.length === 0 || nums.length > 5) return null;
  if (nums.some((n) => !Number.isFinite(n) || n <= 0)) return null;
  return nums;
}

/** Status banner used by every Save button. */
type SaveStatus = 'idle' | 'saving' | 'saved' | { error: string };

// =====================================================
// Grouped-list helpers (shared by Machines / Vendors / Materials tabs)
// =====================================================

/** One bucket in a grouped list. */
interface GroupBucket<T> {
  /** Canonical key — used as React key and the `tag` arg to reorderInGroup
   *  when the active grouping is by tag. Empty string = untagged bucket. */
  key: string;
  /** Display label, e.g. "MILL", "Untagged", "$300/hr+". */
  label: string;
  items: T[];
  /** True when this bucket represents the "untagged" sink (always sorts last). */
  untagged: boolean;
}

/**
 * Generic group-by-key. Items hitting the keyFn === null go into the
 * "untagged" bucket (label given by `untaggedLabel`, always last).
 * Buckets are sorted alphabetically by label for a stable display order.
 *
 * Trailing underscore on the name so it doesn't collide with `groupBy` used
 * as a piece of local state inside each tab component.
 */
function groupBy_<T>(
  items: T[],
  keyFn: (item: T) => { key: string; label: string } | null,
  untaggedLabel = 'Untagged',
): GroupBucket<T>[] {
  const map = new Map<string, GroupBucket<T>>();
  for (const item of items) {
    const k = keyFn(item);
    if (k === null) {
      const u = map.get('') ?? { key: '', label: untaggedLabel, items: [], untagged: true };
      u.items.push(item);
      map.set('', u);
    } else {
      const existing = map.get(k.key) ?? {
        key: k.key,
        label: k.label,
        items: [],
        untagged: false,
      };
      existing.items.push(item);
      map.set(k.key, existing);
    }
  }
  const out = Array.from(map.values());
  // Sort: real groups by label (alpha, case-insensitive), untagged always last.
  out.sort((a, b) => {
    if (a.untagged !== b.untagged) return a.untagged ? 1 : -1;
    return a.label.localeCompare(b.label);
  });
  return out;
}

/** Header bar for one group — clickable to collapse. */
function GroupHeader({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full text-left text-xs font-semibold text-text-mute uppercase tracking-wide mb-2 flex items-center gap-2 hover:text-orange"
    >
      <span className="inline-block w-3">{collapsed ? '▶' : '▼'}</span>
      <span>{label}</span>
      <span className="text-text-mute font-normal">({count})</span>
    </button>
  );
}

/** ↑ / ↓ reorder buttons rendered to the left of every list row. */
function ReorderButtons({
  onUp,
  onDown,
  disabledUp,
  disabledDown,
}: {
  onUp: () => void;
  onDown: () => void;
  disabledUp: boolean;
  disabledDown: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 shrink-0">
      <button
        type="button"
        onClick={onUp}
        disabled={disabledUp}
        aria-label="Move up"
        className="px-1 text-xs text-text-mute hover:text-orange disabled:opacity-30 disabled:hover:text-text-mute leading-none"
      >
        ▲
      </button>
      <button
        type="button"
        onClick={onDown}
        disabled={disabledDown}
        aria-label="Move down"
        className="px-1 text-xs text-text-mute hover:text-orange disabled:opacity-30 disabled:hover:text-text-mute leading-none"
      >
        ▼
      </button>
    </div>
  );
}

function SaveButton({
  status,
  disabled,
  onClick,
  label = 'Save',
}: {
  status: SaveStatus;
  disabled?: boolean;
  onClick: () => void;
  label?: string;
}) {
  const isSaving = status === 'saving';
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || isSaving}
        className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
      >
        {isSaving ? 'Saving…' : label}
      </button>
      {status === 'saved' && (
        <span className="text-xs text-green font-semibold">Saved ✓</span>
      )}
      {typeof status === 'object' && 'error' in status && (
        <span className="text-xs text-orange">⚠ {status.error}</span>
      )}
    </div>
  );
}

// =====================================================
// Page shell
// =====================================================

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('general');
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Settings loaded once per mount. Each tab's Save handler refreshes from
  // Firebase after writing so subsequent edits see the latest record (and
  // any concurrent write from another tab / device).
  async function load() {
    setLoadError(null);
    try {
      const s = await getUserSettings();
      setSettings(s);
    } catch (e) {
      setLoadError(`Failed to load settings: ${(e as Error).message}`);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {loadError}</span>
          <button
            type="button"
            onClick={load}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
          Loading settings…
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-navy mb-1">Settings</h1>
      <p className="text-sm text-text-mute mb-4">
        Personal preferences for this user — saved per-account.
      </p>

      {/* Tabs */}
      <div className="border-b border-line mb-4 flex flex-wrap">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? 'border-orange text-orange'
                  : 'border-transparent text-text-mute hover:text-navy'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'general' && (
        <GeneralTab settings={settings} onChanged={load} />
      )}
      {tab === 'defaults' && (
        <DefaultsTab settings={settings} onChanged={load} />
      )}
      {tab === 'vendors' && <VendorsTab />}
      {tab === 'machines' && <MachinesTab />}
      {tab === 'materials' && <MaterialsTab />}
      {tab === 'jemma' && (
        <JemmaTab settings={settings} onChanged={load} />
      )}
      {tab === 'account' && <AccountTab />}
    </div>
  );
}

// =====================================================
// General tab
// =====================================================

interface TabProps {
  settings: UserSettings;
  onChanged: () => Promise<void>;
}

function GeneralTab({ settings, onChanged }: TabProps) {
  // Local form state — initialized from props but NOT kept in sync. The user
  // is editing a draft until they hit Save; pulling props back in mid-edit
  // would clobber their inputs.
  const [tiersRaw, setTiersRaw] = useState(
    settings.defaultQuoteTiers?.join(' / ') ?? DEFAULT_TIERS_FALLBACK,
  );
  const [marginRaw, setMarginRaw] = useState(
    (settings.marginWarningThreshold ?? DEFAULT_MARGIN_WARN).toString(),
  );
  const [jobType, setJobType] = useState<JobType>(
    settings.defaultJobType ?? 'multi_part_production',
  );
  const [status, setStatus] = useState<SaveStatus>('idle');

  async function handleSave() {
    setStatus('saving');
    const tiers = parseTiers(tiersRaw);
    if (!tiers) {
      setStatus({
        error: 'Qty tiers must be 1-5 positive integers (e.g. "10 / 50 / 100").',
      });
      return;
    }
    const margin = Number.parseFloat(marginRaw);
    if (!Number.isFinite(margin) || margin < 0 || margin > 100) {
      setStatus({ error: 'Margin warning threshold must be a number between 0 and 100.' });
      return;
    }
    try {
      await updateUserSettings({
        defaultQuoteTiers: tiers,
        marginWarningThreshold: margin,
        defaultJobType: jobType,
      });
      await onChanged();
      setStatus('saved');
    } catch (e) {
      setStatus({ error: `Failed to save: ${(e as Error).message}` });
    }
  }

  return (
    <div className="bg-paper border border-line rounded p-5 space-y-4">
      <h2 className="font-semibold text-navy">General preferences</h2>

      <Field
        label="Default quote tiers"
        help='Pre-fills new parts and quotes. 1-5 positive integers, separated by slashes, commas, or spaces. e.g. "10 / 50 / 100".'
      >
        <input
          type="text"
          value={tiersRaw}
          onChange={(e) => setTiersRaw(e.target.value)}
          placeholder={DEFAULT_TIERS_FALLBACK}
          className="w-full max-w-xs px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
        />
      </Field>

      <Field
        label="Margin warning threshold (%)"
        help="Quotes below this margin will surface a warning during review."
      >
        <input
          type="number"
          value={marginRaw}
          onChange={(e) => setMarginRaw(e.target.value)}
          min={0}
          max={100}
          step={1}
          className="w-full max-w-[100px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
        />
      </Field>

      <Field
        label="Default job type"
        help="Pre-selected on the new-quote modal. You can always override."
      >
        <select
          value={jobType}
          onChange={(e) => setJobType(e.target.value as JobType)}
          className="w-full max-w-xs px-2 py-1.5 border border-line-dark rounded text-sm bg-white focus:outline-none focus:border-orange"
        >
          {JOB_TYPES.map((j) => (
            <option key={j.value} value={j.value}>
              {j.label}
            </option>
          ))}
        </select>
      </Field>

      <div className="pt-2 border-t border-line">
        <SaveButton status={status} onClick={handleSave} />
      </div>
    </div>
  );
}

// =====================================================
// Defaults tab — collar shop overrides
// =====================================================

/** Numeric field metadata for the collar form. Defined once so the form can
 *  iterate without 8 copies of the same JSX. */
interface NumField {
  key: keyof CollarDefaultsShape;
  label: string;
  unit: string;
  step: number;
  help?: string;
}

const COLLAR_FIELDS: NumField[] = [
  { key: 'feedIpm', label: 'Feed rate', unit: 'IPM', step: 1, help: 'Router feed on phenolic.' },
  { key: 'doc', label: 'Depth of cut', unit: 'in', step: 0.01, help: 'Per pass.' },
  { key: 'spacing', label: 'Inter-part spacing (kerf)', unit: 'in', step: 0.01 },
  { key: 'sheetLen', label: 'Sheet length (long axis)', unit: 'in', step: 1 },
  { key: 'sheetWid', label: 'Sheet width (short axis)', unit: 'in', step: 1 },
  { key: 'loadUnloadMin', label: 'Load + unload', unit: 'min', step: 0.5 },
  { key: 'screwDownMin', label: 'Screw-down to table', unit: 'min', step: 0.5 },
  { key: 'hourlyRate', label: 'Hourly rate (burdened)', unit: '$/hr', step: 1 },
];

function DefaultsTab({ settings, onChanged }: TabProps) {
  // Initial values — user override wins, otherwise fall through to COLLAR_DEFAULTS.
  // Kept as strings so the user can type a partial value (e.g. "0.") without us
  // coercing back to a number on every keystroke.
  const initial = useMemo<Record<keyof CollarDefaultsShape, string>>(() => {
    const o = settings.collarDefaults ?? {};
    return {
      feedIpm: (o.feedIpm ?? COLLAR_DEFAULTS.feedIpm).toString(),
      doc: (o.doc ?? COLLAR_DEFAULTS.doc).toString(),
      spacing: (o.spacing ?? COLLAR_DEFAULTS.spacing).toString(),
      sheetLen: (o.sheetLen ?? COLLAR_DEFAULTS.sheetLen).toString(),
      sheetWid: (o.sheetWid ?? COLLAR_DEFAULTS.sheetWid).toString(),
      hourlyRate: (o.hourlyRate ?? COLLAR_DEFAULTS.hourlyRate).toString(),
      loadUnloadMin: (o.loadUnloadMin ?? COLLAR_DEFAULTS.loadUnloadMin).toString(),
      screwDownMin: (o.screwDownMin ?? COLLAR_DEFAULTS.screwDownMin).toString(),
    };
  }, [settings.collarDefaults]);

  const [values, setValues] = useState(initial);
  const [status, setStatus] = useState<SaveStatus>('idle');

  function set(key: keyof CollarDefaultsShape, v: string) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function handleSave() {
    setStatus('saving');
    // Parse all fields; bail with the first bad one so the user sees one error
    // at a time (rather than a comma-separated wall).
    const parsed: Partial<CollarDefaultsShape> = {};
    for (const f of COLLAR_FIELDS) {
      const n = Number.parseFloat(values[f.key]);
      if (!Number.isFinite(n) || n < 0) {
        setStatus({ error: `${f.label} must be a non-negative number.` });
        return;
      }
      parsed[f.key] = n;
    }
    try {
      await updateUserSettings({ collarDefaults: parsed });
      await onChanged();
      setStatus('saved');
    } catch (e) {
      setStatus({ error: `Failed to save: ${(e as Error).message}` });
    }
  }

  function handleReset() {
    // "Reset to shop defaults" — wipes the override so getCollarDefaults() falls
    // back to COLLAR_DEFAULTS. We write {} (not undefined) so a stale override
    // can't survive in Firebase.
    void (async () => {
      setStatus('saving');
      try {
        await updateUserSettings({ collarDefaults: {} });
        await onChanged();
        // Re-seed local form from the fresh COLLAR_DEFAULTS for immediate feedback.
        setValues({
          feedIpm: COLLAR_DEFAULTS.feedIpm.toString(),
          doc: COLLAR_DEFAULTS.doc.toString(),
          spacing: COLLAR_DEFAULTS.spacing.toString(),
          sheetLen: COLLAR_DEFAULTS.sheetLen.toString(),
          sheetWid: COLLAR_DEFAULTS.sheetWid.toString(),
          hourlyRate: COLLAR_DEFAULTS.hourlyRate.toString(),
          loadUnloadMin: COLLAR_DEFAULTS.loadUnloadMin.toString(),
          screwDownMin: COLLAR_DEFAULTS.screwDownMin.toString(),
        });
        setStatus('saved');
      } catch (e) {
        setStatus({ error: `Failed to reset: ${(e as Error).message}` });
      }
    })();
  }

  return (
    <div className="bg-paper border border-line rounded p-5 space-y-4">
      <div>
        <h2 className="font-semibold text-navy">Collar shop defaults</h2>
        <p className="text-xs text-text-mute mt-1">
          Used by the collar wizard for cut-time + nesting estimates. Per-field overrides —
          unset fields fall back to the built-in defaults.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        {COLLAR_FIELDS.map((f) => (
          <NumberField
            key={f.key}
            label={f.label}
            unit={f.unit}
            step={f.step}
            help={f.help}
            value={values[f.key]}
            onChange={(v) => set(f.key, v)}
          />
        ))}
      </div>

      <div className="pt-2 border-t border-line flex items-center gap-3 flex-wrap">
        <SaveButton status={status} onClick={handleSave} />
        <button
          type="button"
          onClick={handleReset}
          className="text-xs text-text-mute hover:text-orange hover:underline"
        >
          Reset to shop defaults
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Jemma tab — capability toggles, grouped by category
// =====================================================

function JemmaTab({ settings, onChanged }: TabProps) {
  // Snapshot the registry once per render — listJemmaCapabilities reflects
  // module load state, so any module that registered (or HMR-re-registered)
  // before this render shows up automatically. No re-fetch needed on toggle.
  const capabilities = listJemmaCapabilities();
  const grouped = useMemo(() => {
    const m = new Map<JemmaCapabilityCategory, JemmaCapability[]>();
    for (const c of capabilities) {
      const arr = m.get(c.category) ?? [];
      arr.push(c);
      m.set(c.category, arr);
    }
    return m;
  }, [capabilities]);

  // Local map of pending toggle state. Initialized from existing grants +
  // defaults; mutated on click; persisted on Save.
  const [grants, setGrants] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    for (const c of capabilities) {
      out[c.id] = settings.jemmaGrants?.[c.id] ?? c.defaultGranted;
    }
    return out;
  });
  const [status, setStatus] = useState<SaveStatus>('idle');

  function toggle(id: string) {
    setGrants((prev) => ({ ...prev, [id]: !prev[id] }));
    // Mutating clears the saved-confirmation so the user can see their edit
    // is pending again.
    setStatus('idle');
  }

  async function handleSave() {
    setStatus('saving');
    try {
      await updateUserSettings({ jemmaGrants: grants });
      await onChanged();
      setStatus('saved');
    } catch (e) {
      setStatus({ error: `Failed to save: ${(e as Error).message}` });
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-paper border border-line rounded p-5">
        <h2 className="font-semibold text-navy">What Jemma can do</h2>
        <p className="text-xs text-text-mute mt-1">
          Toggle individual capabilities. Anything off is hard-disabled — Jemma will tell you
          it's not allowed rather than silently skip.
        </p>
      </div>

      {CATEGORY_ORDER.map((cat) => {
        const list = grouped.get(cat);
        if (!list || list.length === 0) return null;
        return (
          <section key={cat} className="bg-paper border border-line rounded p-5">
            <h3 className="text-sm font-semibold text-navy uppercase tracking-wide mb-3">
              {CATEGORY_LABELS[cat]}
            </h3>
            <div className="space-y-3">
              {list.map((c) => (
                <CapabilityRow
                  key={c.id}
                  capability={c}
                  granted={grants[c.id] ?? c.defaultGranted}
                  onToggle={() => toggle(c.id)}
                />
              ))}
            </div>
          </section>
        );
      })}

      <div className="bg-paper border border-line rounded p-5">
        <SaveButton status={status} onClick={handleSave} label="Save grants" />
      </div>
    </div>
  );
}

function CapabilityRow({
  capability,
  granted,
  onToggle,
}: {
  capability: JemmaCapability;
  granted: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <Toggle granted={granted} onToggle={onToggle} label={capability.label} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-navy">{capability.label}</div>
        <div className="text-xs text-text-mute">{capability.description}</div>
        {capability.requires && (
          <div className="text-[11px] text-text-mute italic mt-0.5">
            Needs: {capability.requires}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Tailwind-only toggle. role="switch" + aria-checked for a11y; clicking the
 * label or the pill both work. Keyed off `granted` so the parent owns truth.
 */
function Toggle({
  granted,
  onToggle,
  label,
}: {
  granted: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={granted}
      aria-label={label}
      onClick={onToggle}
      className={`shrink-0 inline-flex items-center w-10 h-5 rounded-full transition-colors mt-0.5 ${
        granted ? 'bg-orange' : 'bg-line-dark'
      }`}
    >
      <span
        className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
          granted ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// =====================================================
// Account tab
// =====================================================

function AccountTab() {
  // Live-track the user so a sign-out from another tab updates this UI before
  // App.tsx's AuthGate kicks us back to LoginPage.
  const [user, setUser] = useState(currentFirebaseUser());
  const [resetStatus, setResetStatus] = useState<
    'idle' | 'sending' | 'sent' | { error: string }
  >('idle');
  const [signOutBusy, setSignOutBusy] = useState(false);

  useEffect(() => {
    return observeAuthState((u) => setUser(u));
  }, []);

  async function handleSendReset() {
    if (!user?.email) {
      setResetStatus({ error: 'No email on file for this account.' });
      return;
    }
    setResetStatus('sending');
    try {
      // sendPasswordResetEmail is fully wired — fires off a real Firebase email
      // to the user's address. We don't gate on Firebase's domain whitelist
      // here; if the domain isn't approved in Firebase Console > Auth >
      // Templates, Firebase returns an error which we surface inline.
      await sendPasswordResetEmail(auth, user.email);
      setResetStatus('sent');
    } catch (e) {
      setResetStatus({ error: (e as Error).message });
    }
  }

  async function handleSignOut() {
    if (signOutBusy) return;
    setSignOutBusy(true);
    try {
      await firebaseSignOut();
      // App.tsx AuthGate flips to LoginPage on the next render — no nav needed.
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SettingsPage] sign-out failed', e);
      setSignOutBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-paper border border-line rounded p-5 space-y-3">
        <h2 className="font-semibold text-navy">Account</h2>
        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-0.5">
            Signed in as
          </div>
          <div className="font-mono text-sm">{user?.email ?? user?.uid ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-0.5">
            User ID
          </div>
          <div className="font-mono text-[11px] text-text-mute break-all">
            {user?.uid ?? '—'}
          </div>
        </div>
      </div>

      <div className="bg-paper border border-line rounded p-5 space-y-3">
        <h3 className="font-semibold text-navy">Change password</h3>
        <p className="text-xs text-text-mute">
          Firebase will email a reset link to{' '}
          <span className="font-mono">{user?.email ?? 'your account'}</span>.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSendReset}
            disabled={resetStatus === 'sending' || !user?.email}
            className="px-3 py-1.5 border border-line-dark rounded text-sm font-semibold text-navy hover:bg-bg disabled:opacity-50"
          >
            {resetStatus === 'sending' ? 'Sending…' : 'Send password reset email'}
          </button>
          {resetStatus === 'sent' && (
            <span className="text-xs text-green font-semibold">
              Sent ✓ — check your inbox.
            </span>
          )}
          {typeof resetStatus === 'object' && 'error' in resetStatus && (
            <span className="text-xs text-orange">⚠ {resetStatus.error}</span>
          )}
        </div>
      </div>

      <div className="bg-paper border border-orange/30 rounded p-5">
        <h3 className="font-semibold text-navy mb-1">Sign out</h3>
        <p className="text-xs text-text-mute mb-3">
          Ends your session on this device. You'll be back at the login screen.
        </p>
        <button
          type="button"
          onClick={handleSignOut}
          disabled={signOutBusy}
          className="px-4 py-2 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {signOutBusy ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Vendors tab — outside-service / material supplier matrix
// =====================================================

/** Service-filter chips. 'all' is the default; the rest map to VendorServiceType. */
type ServiceFilter = 'all' | VendorServiceType;

/** Most-used services surface as chips; the rest hide behind a "More..." popout
 *  to keep the row scannable. Order matters — these are the ones Justin uses
 *  most for outside services today. */
const PRIMARY_SERVICE_FILTERS: VendorServiceType[] = [
  'laser_cutting',
  'plasma_cutting',
  'waterjet_cutting',
  'painting',
  'powder_coating',
  'anodizing',
  'black_oxide',
  'heat_treat',
  'material_supply',
];

const SECONDARY_SERVICE_FILTERS: VendorServiceType[] = [
  'grinding',
  'broaching',
  'edm',
  'plating_zinc',
  'plating_nickel',
  'plating_chrome',
  'passivation',
  'deburring',
  'polishing',
  'tumbling',
  'silk_screen',
  'engraving',
  'other',
];

/** Group-by options offered on the Vendors tab. */
const VENDOR_GROUP_OPTIONS = [
  { value: 'tag', label: 'First tag' },
  { value: 'service', label: 'First service' },
  { value: 'favorite', label: 'Favorites first' },
  { value: 'none', label: 'No grouping' },
];

function VendorsTab() {
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchRaw, setSearchRaw] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [filter, setFilter] = useState<ServiceFilter>('all');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  // Either 'new' (creating) or a vendor id (editing). null = neither (closed).
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [groupBy, setGroupBy] = useState<string>('tag');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Debounce the search box — 200ms is fast enough to feel live but cuts the
  // per-keystroke re-filter on long vendor lists.
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchRaw), 200);
    return () => window.clearTimeout(t);
  }, [searchRaw]);

  async function load() {
    setLoadError(null);
    try {
      const list = await listVendors();
      setVendors(list);
    } catch (e) {
      setLoadError(`Failed to load vendors: ${(e as Error).message}`);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Union of all tags across vendors — feeds TagInput's suggestion dropdown.
  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const v of vendors ?? []) {
      for (const t of v.tags ?? []) s.add(t);
    }
    return Array.from(s).sort();
  }, [vendors]);

  const filtered = useMemo(() => {
    if (!vendors) return [];
    const needle = searchDebounced.trim().toLowerCase();
    return vendors.filter((v) => {
      if (filter !== 'all' && !v.services?.includes(filter)) return false;
      if (needle && !v.name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [vendors, filter, searchDebounced]);

  // Group buckets based on `groupBy`. "No grouping" returns a single bucket
  // labelled "Vendors" — same code path, just no header collapse useful behavior.
  const groups = useMemo<GroupBucket<Vendor>[]>(() => {
    if (groupBy === 'tag') {
      return groupBy_(filtered, (v) => {
        const first = (v.tags ?? [])[0];
        return first ? { key: first, label: first.toUpperCase() } : null;
      });
    }
    if (groupBy === 'service') {
      return groupBy_(filtered, (v) => {
        const first = v.services?.[0];
        return first
          ? { key: first, label: VENDOR_SERVICE_LABELS[first] }
          : null;
      });
    }
    if (groupBy === 'favorite') {
      return groupBy_(filtered, (v) =>
        v.favorite
          ? { key: 'fav', label: '★ Favorites' }
          : { key: 'rest', label: 'Other vendors' },
      );
    }
    // 'none' — single bucket, no header
    return [{ key: '__all__', label: 'All vendors', items: filtered, untagged: false }];
  }, [filtered, groupBy]);

  if (loadError) {
    return (
      <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
        <span>⚠ {loadError}</span>
        <button
          type="button"
          onClick={load}
          className="text-xs text-orange font-semibold hover:underline shrink-0"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!vendors) {
    return (
      <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
        Loading vendors…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-paper border border-line rounded p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-navy">Vendors</h2>
            <p className="text-xs text-text-mute mt-1">
              Outside-service shops and material suppliers. Define them here so quotes
              can pick from this list instead of typing freeform.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold shrink-0"
          >
            + Add vendor
          </button>
        </div>

        {/* Search + filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="search"
              placeholder="Search by name…"
              value={searchRaw}
              onChange={(e) => setSearchRaw(e.target.value)}
              className="flex-1 min-w-[200px] max-w-md px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
            <GroupedListControls
              groupBy={groupBy}
              options={VENDOR_GROUP_OPTIONS}
              onChange={setGroupBy}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <ServiceChip
              label="All"
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {PRIMARY_SERVICE_FILTERS.map((s) => (
              <ServiceChip
                key={s}
                label={VENDOR_SERVICE_LABELS[s]}
                active={filter === s}
                onClick={() => setFilter(s)}
              />
            ))}
            <button
              type="button"
              onClick={() => setShowMoreFilters((v) => !v)}
              className="px-2.5 py-1 text-xs text-text-mute hover:text-orange font-semibold"
            >
              {showMoreFilters ? 'Less…' : 'More…'}
            </button>
          </div>
          {showMoreFilters && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {SECONDARY_SERVICE_FILTERS.map((s) => (
                <ServiceChip
                  key={s}
                  label={VENDOR_SERVICE_LABELS[s]}
                  active={filter === s}
                  onClick={() => setFilter(s)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add-new form (expands in place when "+ Add vendor" is hit) */}
      {editing === 'new' && (
        <VendorEditForm
          initial={null}
          existingTags={allTags}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {/* Vendor list — grouped */}
      {filtered.length === 0 ? (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <p className="text-sm text-text-mute">
            {searchDebounced || filter !== 'all'
              ? 'No vendors match this filter.'
              : 'No vendors yet. Add one to use them in outside-services on quotes.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = collapsed[group.key] ?? false;
            const showHeader = groupBy !== 'none';
            return (
              <div key={group.key}>
                {showHeader && (
                  <GroupHeader
                    label={group.label}
                    count={group.items.length}
                    collapsed={isCollapsed}
                    onToggle={() =>
                      setCollapsed((p) => ({ ...p, [group.key]: !isCollapsed }))
                    }
                  />
                )}
                {!isCollapsed && (
                  <div className="space-y-2">
                    {group.items.map((v, idx) =>
                      editing === v.id ? (
                        <VendorEditForm
                          key={v.id}
                          initial={v}
                          existingTags={allTags}
                          onCancel={() => setEditing(null)}
                          onSaved={async () => {
                            setEditing(null);
                            await load();
                          }}
                        />
                      ) : (
                        <VendorRow
                          key={v.id}
                          vendor={v}
                          showReorder={groupBy === 'tag'}
                          isFirst={idx === 0}
                          isLast={idx === group.items.length - 1}
                          onReorder={async (dir) => {
                            // When grouping by tag, untagged bucket → tag=undefined.
                            const tag = group.untagged ? undefined : group.key;
                            await reorderVendorInGroup(v.id, dir, tag);
                            await load();
                          }}
                          onEdit={() => setEditing(v.id)}
                          onDeleted={load}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ServiceChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? 'bg-orange text-white border-orange'
          : 'bg-paper text-text border-line-dark hover:border-orange'
      }`}
    >
      {label}
    </button>
  );
}

function VendorRow({
  vendor,
  showReorder,
  isFirst,
  isLast,
  onReorder,
  onEdit,
  onDeleted,
}: {
  vendor: Vendor;
  showReorder: boolean;
  isFirst: boolean;
  isLast: boolean;
  onReorder: (dir: 'up' | 'down') => Promise<void>;
  onEdit: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    // Per Justin's preference, no native confirm() — the inline delete chip
    // becomes a two-step Are-you-sure to keep the page free of system dialogs.
    if (!window.confirm(`Delete vendor "${vendor.name}"? (soft-delete — can be restored)`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await softDeleteVendor(vendor.id, 'deleted from Settings');
      await onDeleted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function toggleFavorite() {
    setBusy(true);
    setError(null);
    try {
      await updateVendor(vendor.id, { favorite: !vendor.favorite });
      await onDeleted(); // reuses parent reload
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReorder(dir: 'up' | 'down') {
    setBusy(true);
    setError(null);
    try {
      await onReorder(dir);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-paper border border-line rounded p-3">
      <div className="flex items-start gap-3">
        {showReorder && (
          <ReorderButtons
            onUp={() => void handleReorder('up')}
            onDown={() => void handleReorder('down')}
            disabledUp={isFirst || busy}
            disabledDown={isLast || busy}
          />
        )}
        <button
          type="button"
          onClick={toggleFavorite}
          disabled={busy}
          aria-label={vendor.favorite ? 'Unfavorite vendor' : 'Favorite vendor'}
          className={`text-lg leading-none mt-0.5 transition-colors ${
            vendor.favorite ? 'text-orange' : 'text-line-dark hover:text-orange'
          }`}
        >
          {vendor.favorite ? '★' : '☆'}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-semibold text-navy">{vendor.name}</div>
            {vendor.importedFromV1 && (
              <span className="text-[10px] uppercase tracking-wide text-text-mute font-mono">
                imported
              </span>
            )}
          </div>

          {/* Tag pills (Justin's freeform tags) */}
          {vendor.tags && vendor.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {vendor.tags.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 bg-orange/10 border border-orange/30 rounded text-[11px] text-orange font-semibold"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Service pills */}
          <div className="flex flex-wrap gap-1 mt-1">
            {(vendor.services ?? []).map((s) => (
              <span
                key={s}
                className="px-1.5 py-0.5 bg-bg border border-line rounded text-[11px] text-text"
              >
                {VENDOR_SERVICE_LABELS[s]}
              </span>
            ))}
          </div>

          {/* Inline meta — contact / lead time / markup. Compact one-liner. */}
          <div className="text-xs text-text-mute mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {vendor.contactName && <span>👤 {vendor.contactName}</span>}
            {vendor.contactEmail && <span>✉ {vendor.contactEmail}</span>}
            {vendor.contactPhone && <span>☎ {vendor.contactPhone}</span>}
            {typeof vendor.leadTimeDays === 'number' && (
              <span>⏱ {vendor.leadTimeDays}d lead</span>
            )}
            {typeof vendor.defaultMarkupPct === 'number' && vendor.defaultMarkupPct > 0 && (
              <span>+{vendor.defaultMarkupPct}% markup</span>
            )}
          </div>

          {error && (
            <div className="text-xs text-orange mt-1">⚠ {error}</div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            className="px-2 py-1 text-xs font-semibold text-navy border border-line-dark rounded hover:bg-bg disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className="px-2 py-1 text-xs font-semibold text-orange border border-orange/40 rounded hover:bg-orange/10 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Form used both for "Add new vendor" and "Edit existing vendor". Pass
 * `initial=null` to create; pass a Vendor record to edit.
 */
function VendorEditForm({
  initial,
  existingTags,
  onCancel,
  onSaved,
}: {
  initial: Vendor | null;
  existingTags: string[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [services, setServices] = useState<VendorServiceType[]>(initial?.services ?? []);
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [contactName, setContactName] = useState(initial?.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [leadTimeDaysRaw, setLeadTimeDaysRaw] = useState(
    initial?.leadTimeDays != null ? String(initial.leadTimeDays) : '',
  );
  const [markupRaw, setMarkupRaw] = useState(
    initial?.defaultMarkupPct != null ? String(initial.defaultMarkupPct) : '',
  );
  const [taxRaw, setTaxRaw] = useState(
    initial?.defaultTaxPct != null ? String(initial.defaultTaxPct) : '',
  );
  const [favorite, setFavorite] = useState<boolean>(initial?.favorite ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [status, setStatus] = useState<SaveStatus>('idle');

  function toggleService(s: VendorServiceType) {
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  function parseOptionalNumber(
    raw: string,
    label: string,
    opts: { min?: number; max?: number } = {},
  ): { ok: true; value: number | undefined } | { ok: false; error: string } {
    if (raw.trim() === '') return { ok: true, value: undefined };
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) return { ok: false, error: `${label} must be a number.` };
    if (opts.min != null && n < opts.min) {
      return { ok: false, error: `${label} must be ≥ ${opts.min}.` };
    }
    if (opts.max != null && n > opts.max) {
      return { ok: false, error: `${label} must be ≤ ${opts.max}.` };
    }
    return { ok: true, value: n };
  }

  async function handleSave() {
    setStatus('saving');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setStatus({ error: 'Vendor name is required.' });
      return;
    }
    if (services.length === 0) {
      setStatus({ error: 'Pick at least one service.' });
      return;
    }
    const lead = parseOptionalNumber(leadTimeDaysRaw, 'Lead time', { min: 0 });
    if (!lead.ok) {
      setStatus({ error: lead.error });
      return;
    }
    const markup = parseOptionalNumber(markupRaw, 'Markup %', { min: 0, max: 1000 });
    if (!markup.ok) {
      setStatus({ error: markup.error });
      return;
    }
    const tax = parseOptionalNumber(taxRaw, 'Tax %', { min: 0, max: 100 });
    if (!tax.ok) {
      setStatus({ error: tax.error });
      return;
    }

    // Build the payload. Empty-string contact fields are dropped (undefined)
    // so Firebase doesn't carry empty-key junk.
    const payload: CreateVendorInput | UpdateVendorInput = {
      name: trimmedName,
      services,
      tags: tags.length > 0 ? tags : undefined,
      contactName: contactName.trim() || undefined,
      contactEmail: contactEmail.trim() || undefined,
      contactPhone: contactPhone.trim() || undefined,
      leadTimeDays: lead.value,
      defaultMarkupPct: markup.value,
      defaultTaxPct: tax.value,
      favorite,
      notes: notes.trim() || undefined,
    };

    try {
      if (initial) {
        await updateVendor(initial.id, payload as UpdateVendorInput);
      } else {
        await createVendor(payload as CreateVendorInput);
      }
      await onSaved();
    } catch (e) {
      setStatus({ error: `Failed to save: ${(e as Error).message}` });
    }
  }

  return (
    <div className="bg-paper border-2 border-orange/40 rounded p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-navy">
          {initial ? `Edit ${initial.name}` : 'Add vendor'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-mute hover:text-orange"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cleveland Laser Cutters"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field
          label="Tags"
          help="Drives grouping in this Settings list (first tag). Type and press Enter."
        >
          <TagInput
            value={tags}
            onChange={setTags}
            suggestions={existingTags}
          />
        </Field>
      </div>

      <Field label="Services" help="Pick everything this vendor does.">
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(VENDOR_SERVICE_LABELS) as VendorServiceType[]).map((s) => {
            const checked = services.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleService(s)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  checked
                    ? 'bg-orange text-white border-orange'
                    : 'bg-paper text-text border-line-dark hover:border-orange'
                }`}
              >
                {VENDOR_SERVICE_LABELS[s]}
              </button>
            );
          })}
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Contact name">
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Contact email">
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Contact phone">
          <input
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Lead time (days)" help="Typical turnaround.">
          <input
            type="number"
            min={0}
            step={1}
            value={leadTimeDaysRaw}
            onChange={(e) => setLeadTimeDaysRaw(e.target.value)}
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field
          label="Default markup %"
          help="Applied to their price on quotes. 0 = pass-through."
        >
          <input
            type="number"
            min={0}
            step={1}
            value={markupRaw}
            onChange={(e) => setMarkupRaw(e.target.value)}
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Default tax %" help="Vendor surcharge, if any.">
          <input
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={taxRaw}
            onChange={(e) => setTaxRaw(e.target.value)}
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Quirks, account #, minimums, etc."
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
        />
      </Field>

      <div className="flex items-center gap-2">
        <input
          id="vendor-fav"
          type="checkbox"
          checked={favorite}
          onChange={(e) => setFavorite(e.target.checked)}
          className="accent-orange"
        />
        <label htmlFor="vendor-fav" className="text-sm text-text">
          Favorite — surface at the top of pickers
        </label>
      </div>

      <div className="pt-2 border-t border-line flex items-center gap-3">
        <SaveButton
          status={status}
          onClick={handleSave}
          label={initial ? 'Save changes' : 'Add vendor'}
        />
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-mute hover:text-orange"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Machines tab — shop machines and hourly rates
// =====================================================

/** Group-by options offered on the Machines tab. */
const MACHINE_GROUP_OPTIONS = [
  { value: 'tag', label: 'First tag' },
  { value: 'rate', label: 'Run rate (high → low)' },
  { value: 'none', label: 'No grouping' },
];

/** Bucket boundaries for the "Run rate" grouping. */
function machineRateBucket(runRate: number): { key: string; label: string } {
  if (runRate >= 300) return { key: 'r300', label: '$300+/hr' };
  if (runRate >= 200) return { key: 'r200', label: '$200–299/hr' };
  if (runRate >= 100) return { key: 'r100', label: '$100–199/hr' };
  return { key: 'r0', label: 'Under $100/hr' };
}

function MachinesTab() {
  const [machines, setMachines] = useState<Machine[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchRaw, setSearchRaw] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  // Either 'new' (creating) or a machine id (editing). null = neither (closed).
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [groupBy, setGroupBy] = useState<string>('tag');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Same 200ms debounce as the Vendors tab.
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchRaw), 200);
    return () => window.clearTimeout(t);
  }, [searchRaw]);

  async function load() {
    setLoadError(null);
    try {
      const list = await listMachines();
      setMachines(list);
    } catch (e) {
      setLoadError(`Failed to load machines: ${(e as Error).message}`);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Union of all tags — feeds TagInput's suggestion dropdown.
  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const m of machines ?? []) {
      for (const t of m.tags ?? []) s.add(t);
    }
    return Array.from(s).sort();
  }, [machines]);

  const filtered = useMemo(() => {
    if (!machines) return [];
    const needle = searchDebounced.trim().toLowerCase();
    if (!needle) return machines;
    return machines.filter((m) => m.name.toLowerCase().includes(needle));
  }, [machines, searchDebounced]);

  const groups = useMemo<GroupBucket<Machine>[]>(() => {
    if (groupBy === 'tag') {
      return groupBy_(filtered, (m) => {
        const first = (m.tags ?? [])[0];
        return first ? { key: first, label: first.toUpperCase() } : null;
      });
    }
    if (groupBy === 'rate') {
      // Rate buckets — order by descending rate (highest tier first).
      const out = groupBy_(filtered, (m) => machineRateBucket(m.runRate));
      const rateOrder: Record<string, number> = { r300: 0, r200: 1, r100: 2, r0: 3 };
      out.sort((a, b) => (rateOrder[a.key] ?? 99) - (rateOrder[b.key] ?? 99));
      return out;
    }
    // 'none' — single bucket, no header
    return [{ key: '__all__', label: 'All machines', items: filtered, untagged: false }];
  }, [filtered, groupBy]);

  if (loadError) {
    return (
      <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
        <span>⚠ {loadError}</span>
        <button
          type="button"
          onClick={load}
          className="text-xs text-orange font-semibold hover:underline shrink-0"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!machines) {
    return (
      <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
        Loading machines…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-paper border border-line rounded p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-navy">Machines</h2>
            <p className="text-xs text-text-mute mt-1">
              Shop machines and their hourly rates. Used by the part wizard to estimate
              run + setup cost.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold shrink-0"
          >
            + Add machine
          </button>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="search"
            placeholder="Search by name…"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            className="flex-1 min-w-[200px] max-w-md px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <GroupedListControls
            groupBy={groupBy}
            options={MACHINE_GROUP_OPTIONS}
            onChange={setGroupBy}
          />
        </div>
      </div>

      {/* Add-new form (expands in place when "+ Add machine" is hit) */}
      {editing === 'new' && (
        <MachineEditForm
          initial={null}
          existingTags={allTags}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {/* Machine list — grouped */}
      {filtered.length === 0 ? (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <p className="text-sm text-text-mute">
            {searchDebounced
              ? 'No machines match this search.'
              : "No machines yet. Add the shop's machines to use them in part wizard setups."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = collapsed[group.key] ?? false;
            const showHeader = groupBy !== 'none';
            return (
              <div key={group.key}>
                {showHeader && (
                  <GroupHeader
                    label={group.label}
                    count={group.items.length}
                    collapsed={isCollapsed}
                    onToggle={() =>
                      setCollapsed((p) => ({ ...p, [group.key]: !isCollapsed }))
                    }
                  />
                )}
                {!isCollapsed && (
                  <div className="space-y-2">
                    {group.items.map((m, idx) =>
                      editing === m.id ? (
                        <MachineEditForm
                          key={m.id}
                          initial={m}
                          existingTags={allTags}
                          onCancel={() => setEditing(null)}
                          onSaved={async () => {
                            setEditing(null);
                            await load();
                          }}
                        />
                      ) : (
                        <MachineRow
                          key={m.id}
                          machine={m}
                          showReorder={groupBy === 'tag'}
                          isFirst={idx === 0}
                          isLast={idx === group.items.length - 1}
                          onReorder={async (dir) => {
                            const tag = group.untagged ? undefined : group.key;
                            await reorderMachineInGroup(m.id, dir, tag);
                            await load();
                          }}
                          onEdit={() => setEditing(m.id)}
                          onChanged={load}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MachineRow({
  machine,
  showReorder,
  isFirst,
  isLast,
  onReorder,
  onEdit,
  onChanged,
}: {
  machine: Machine;
  showReorder: boolean;
  isFirst: boolean;
  isLast: boolean;
  onReorder: (dir: 'up' | 'down') => Promise<void>;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isActive = machine.active !== false;

  async function handleDelete() {
    if (!window.confirm(`Delete machine "${machine.name}"? (soft-delete — can be restored)`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await softDeleteMachine(machine.id, 'deleted from Settings');
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function toggleActive() {
    setBusy(true);
    setError(null);
    try {
      await updateMachine(machine.id, { active: !isActive });
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReorder(dir: 'up' | 'down') {
    setBusy(true);
    setError(null);
    try {
      await onReorder(dir);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Compact rate display — show "run / setup $/hr". When they're equal we
  // collapse to a single number to reduce noise.
  const ratesLabel =
    machine.runRate === machine.setupRate
      ? `$${machine.runRate}/hr`
      : `$${machine.runRate}/hr run · $${machine.setupRate}/hr setup`;

  return (
    <div className={`bg-paper border border-line rounded p-3 ${isActive ? '' : 'opacity-60'}`}>
      <div className="flex items-start gap-3">
        {showReorder && (
          <ReorderButtons
            onUp={() => void handleReorder('up')}
            onDown={() => void handleReorder('down')}
            disabledUp={isFirst || busy}
            disabledDown={isLast || busy}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-semibold text-navy">{machine.name}</div>
            {!isActive && (
              <span className="text-[10px] uppercase tracking-wide text-text-mute font-mono">
                inactive
              </span>
            )}
            {machine.importedFromV1 && (
              <span className="text-[10px] uppercase tracking-wide text-text-mute font-mono">
                imported
              </span>
            )}
          </div>

          {/* Tag pills */}
          {machine.tags && machine.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {machine.tags.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 bg-orange/10 border border-orange/30 rounded text-[11px] text-orange font-semibold"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="text-xs text-text-mute mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="font-mono text-text">{ratesLabel}</span>
            {machine.manufacturer && <span>{machine.manufacturer}</span>}
            {machine.model && <span>Model {machine.model}</span>}
            {machine.serialNumber && <span>S/N {machine.serialNumber}</span>}
            {typeof machine.defaultMarkupPct === 'number' && machine.defaultMarkupPct > 0 && (
              <span>+{machine.defaultMarkupPct}% markup</span>
            )}
          </div>

          {error && <div className="text-xs text-orange mt-1">⚠ {error}</div>}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Toggle
            granted={isActive}
            onToggle={toggleActive}
            label={isActive ? 'Mark inactive' : 'Mark active'}
          />
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            className="px-2 py-1 text-xs font-semibold text-navy border border-line-dark rounded hover:bg-bg disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className="px-2 py-1 text-xs font-semibold text-orange border border-orange/40 rounded hover:bg-orange/10 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Form used both for "Add new machine" and "Edit existing machine". Pass
 * `initial=null` to create; pass a Machine record to edit.
 */
function MachineEditForm({
  initial,
  existingTags,
  onCancel,
  onSaved,
}: {
  initial: Machine | null;
  existingTags: string[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [manufacturer, setManufacturer] = useState(initial?.manufacturer ?? '');
  const [model, setModel] = useState(initial?.model ?? '');
  const [serialNumber, setSerialNumber] = useState(initial?.serialNumber ?? '');
  const [runRateRaw, setRunRateRaw] = useState(
    initial?.runRate != null ? String(initial.runRate) : '',
  );
  const [setupRateRaw, setSetupRateRaw] = useState(
    initial?.setupRate != null ? String(initial.setupRate) : '',
  );
  const [markupRaw, setMarkupRaw] = useState(
    initial?.defaultMarkupPct != null ? String(initial.defaultMarkupPct) : '',
  );
  // Default-active for new machines; preserve whatever the record carries on edit.
  const [active, setActive] = useState<boolean>(initial?.active !== false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [status, setStatus] = useState<SaveStatus>('idle');

  function parseRequiredRate(
    raw: string,
    label: string,
  ): { ok: true; value: number } | { ok: false; error: string } {
    if (raw.trim() === '') return { ok: false, error: `${label} is required.` };
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) {
      return { ok: false, error: `${label} must be a non-negative number.` };
    }
    return { ok: true, value: n };
  }

  function parseOptionalNumber(
    raw: string,
    label: string,
    opts: { min?: number; max?: number } = {},
  ): { ok: true; value: number | undefined } | { ok: false; error: string } {
    if (raw.trim() === '') return { ok: true, value: undefined };
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) return { ok: false, error: `${label} must be a number.` };
    if (opts.min != null && n < opts.min) {
      return { ok: false, error: `${label} must be ≥ ${opts.min}.` };
    }
    if (opts.max != null && n > opts.max) {
      return { ok: false, error: `${label} must be ≤ ${opts.max}.` };
    }
    return { ok: true, value: n };
  }

  async function handleSave() {
    setStatus('saving');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setStatus({ error: 'Machine name is required.' });
      return;
    }
    const runRate = parseRequiredRate(runRateRaw, 'Run rate');
    if (!runRate.ok) {
      setStatus({ error: runRate.error });
      return;
    }
    const setupRate = parseRequiredRate(setupRateRaw, 'Setup rate');
    if (!setupRate.ok) {
      setStatus({ error: setupRate.error });
      return;
    }
    const markup = parseOptionalNumber(markupRaw, 'Markup %', { min: 0, max: 1000 });
    if (!markup.ok) {
      setStatus({ error: markup.error });
      return;
    }

    const payload: CreateMachineInput | UpdateMachineInput = {
      name: trimmedName,
      tags: tags.length > 0 ? tags : undefined,
      runRate: runRate.value,
      setupRate: setupRate.value,
      defaultMarkupPct: markup.value,
      manufacturer: manufacturer.trim() || undefined,
      model: model.trim() || undefined,
      serialNumber: serialNumber.trim() || undefined,
      active,
      notes: notes.trim() || undefined,
    };

    try {
      if (initial) {
        await updateMachine(initial.id, payload as UpdateMachineInput);
      } else {
        await createMachine(payload as CreateMachineInput);
      }
      await onSaved();
    } catch (e) {
      setStatus({ error: `Failed to save: ${(e as Error).message}` });
    }
  }

  return (
    <div className="bg-paper border-2 border-orange/40 rounded p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-navy">
          {initial ? `Edit ${initial.name}` : 'Add machine'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-mute hover:text-orange"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Doosan DNM 750"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field
          label="Tags"
          help="Drives grouping in this Settings list (first tag). Type and press Enter."
        >
          <TagInput
            value={tags}
            onChange={setTags}
            suggestions={existingTags}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Manufacturer">
          <input
            type="text"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            placeholder="e.g. Doosan, Haas, Bridgeport"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Model">
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Serial number">
          <input
            type="text"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field
          label="Default markup %"
          help="Overrides the quote-level markup for this machine. 0 = no override."
        >
          <input
            type="number"
            min={0}
            step={1}
            value={markupRaw}
            onChange={(e) => setMarkupRaw(e.target.value)}
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Run rate ($/hr)" help="Charged while the machine is actively cutting.">
          <input
            type="number"
            min={0}
            step={1}
            value={runRateRaw}
            onChange={(e) => setRunRateRaw(e.target.value)}
            placeholder="e.g. 300"
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field
          label="Setup rate ($/hr)"
          help="Charged during setup. Often equals run rate; tracked separately so it can diverge later."
        >
          <input
            type="number"
            min={0}
            step={1}
            value={setupRateRaw}
            onChange={(e) => setSetupRateRaw(e.target.value)}
            placeholder="e.g. 300"
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Capacity, quirks, max stock size, etc."
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
        />
      </Field>

      <div className="flex items-center gap-2">
        <input
          id="machine-active"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="accent-orange"
        />
        <label htmlFor="machine-active" className="text-sm text-text">
          Active — show in part wizard pickers
        </label>
      </div>

      <div className="pt-2 border-t border-line flex items-center gap-3">
        <SaveButton
          status={status}
          onClick={handleSave}
          label={initial ? 'Save changes' : 'Add machine'}
        />
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-mute hover:text-orange"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Materials tab — stock library + vendor linkage
// =====================================================

/** Category filter type for the Materials tab. */
type CategoryFilter = 'all' | MaterialCategory;

const MATERIAL_FORM_LABELS: Record<MaterialForm, string> = {
  bar: 'Bar',
  round: 'Round',
  plate: 'Plate',
  sheet: 'Sheet',
  tube: 'Tube',
  block: 'Block',
  wire: 'Wire',
  other: 'Other',
};

const PRICE_UNIT_LABELS: Record<MaterialPriceSnapshot['unit'], string> = {
  per_lb: '$/lb',
  per_piece: '$/pc',
  per_bar: '$/bar',
  per_sheet: '$/sheet',
  per_pack: '$/pack',
  per_ft: '$/ft',
};

const PRICE_UNITS: MaterialPriceSnapshot['unit'][] = [
  'per_lb',
  'per_piece',
  'per_bar',
  'per_sheet',
  'per_pack',
  'per_ft',
];

/** Group-by options offered on the Materials tab. */
const MATERIAL_GROUP_OPTIONS = [
  { value: 'tag', label: 'First tag' },
  { value: 'category', label: 'Category' },
  { value: 'none', label: 'No grouping' },
];

function MaterialsTab() {
  const [materials, setMaterials] = useState<Material[] | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchRaw, setSearchRaw] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [filter, setFilter] = useState<CategoryFilter>('all');
  // Either 'new' (creating) or a material id (editing). null = neither (closed).
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [groupBy, setGroupBy] = useState<string>('tag');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Same 200ms debounce as the Vendors tab.
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchRaw), 200);
    return () => window.clearTimeout(t);
  }, [searchRaw]);

  async function load() {
    setLoadError(null);
    try {
      // Load vendors once alongside materials — used by every row to render
      // vendor-name pills from material.vendorIds[].
      const [mList, vList] = await Promise.all([listMaterials(), listVendors()]);
      setMaterials(mList);
      setVendors(vList);
    } catch (e) {
      setLoadError(`Failed to load materials: ${(e as Error).message}`);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Vendor-by-id lookup for row rendering. Built once per vendors[] change.
  const vendorById = useMemo(() => {
    const m = new Map<string, Vendor>();
    for (const v of vendors) m.set(v.id, v);
    return m;
  }, [vendors]);

  // Union of all tags — feeds TagInput's suggestion dropdown.
  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const m of materials ?? []) {
      for (const t of m.tags ?? []) s.add(t);
    }
    return Array.from(s).sort();
  }, [materials]);

  // Per-category counts shown on the filter chips. Built off the unfiltered
  // list so "Aluminum (12)" reflects all materials regardless of search.
  const categoryCounts = useMemo(() => {
    const out: Record<string, number> = { all: materials?.length ?? 0 };
    if (materials) {
      for (const m of materials) {
        out[m.category] = (out[m.category] ?? 0) + 1;
      }
    }
    return out;
  }, [materials]);

  const filtered = useMemo(() => {
    if (!materials) return [];
    const needle = searchDebounced.trim().toLowerCase();
    return materials.filter((m) => {
      if (filter !== 'all' && m.category !== filter) return false;
      if (needle && !m.name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [materials, filter, searchDebounced]);

  const groups = useMemo<GroupBucket<Material>[]>(() => {
    if (groupBy === 'tag') {
      return groupBy_(filtered, (m) => {
        const first = (m.tags ?? [])[0];
        return first ? { key: first, label: first.toUpperCase() } : null;
      });
    }
    if (groupBy === 'category') {
      return groupBy_(filtered, (m) => ({
        key: m.category,
        label: MATERIAL_CATEGORY_LABELS[m.category],
      }));
    }
    // 'none' — single bucket, no header
    return [{ key: '__all__', label: 'All materials', items: filtered, untagged: false }];
  }, [filtered, groupBy]);

  if (loadError) {
    return (
      <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
        <span>⚠ {loadError}</span>
        <button
          type="button"
          onClick={load}
          className="text-xs text-orange font-semibold hover:underline shrink-0"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!materials) {
    return (
      <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
        Loading materials…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-paper border border-line rounded p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-navy">Materials</h2>
            <p className="text-xs text-text-mute mt-1">
              Stock you buy regularly. Linking vendors here lets Jemma route material
              RFQs and surfaces price history on every quote.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold shrink-0"
          >
            + Add material
          </button>
        </div>

        {/* Search + filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="search"
              placeholder="Search by name…"
              value={searchRaw}
              onChange={(e) => setSearchRaw(e.target.value)}
              className="flex-1 min-w-[200px] max-w-md px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
            <GroupedListControls
              groupBy={groupBy}
              options={MATERIAL_GROUP_OPTIONS}
              onChange={setGroupBy}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <CategoryChip
              label={`All${categoryCounts.all ? ` (${categoryCounts.all})` : ''}`}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {(Object.keys(MATERIAL_CATEGORY_LABELS) as MaterialCategory[]).map((cat) => {
              const count = categoryCounts[cat] ?? 0;
              return (
                <CategoryChip
                  key={cat}
                  label={`${MATERIAL_CATEGORY_LABELS[cat]}${count ? ` (${count})` : ''}`}
                  active={filter === cat}
                  inactiveColorClass={MATERIAL_CATEGORY_COLORS[cat]}
                  onClick={() => setFilter(cat)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Add-new form (expands in place when "+ Add material" is hit) */}
      {editing === 'new' && (
        <MaterialEditForm
          initial={null}
          vendors={vendors}
          existingTags={allTags}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {/* Material list — grouped */}
      {filtered.length === 0 ? (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <p className="text-sm text-text-mute">
            {searchDebounced || filter !== 'all'
              ? 'No materials match this filter.'
              : 'No materials yet. Add the stock you buy regularly to track vendors + price history.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const isCollapsed = collapsed[group.key] ?? false;
            const showHeader = groupBy !== 'none';
            return (
              <div key={group.key}>
                {showHeader && (
                  <GroupHeader
                    label={group.label}
                    count={group.items.length}
                    collapsed={isCollapsed}
                    onToggle={() =>
                      setCollapsed((p) => ({ ...p, [group.key]: !isCollapsed }))
                    }
                  />
                )}
                {!isCollapsed && (
                  <div className="space-y-2">
                    {group.items.map((m, idx) =>
                      editing === m.id ? (
                        <MaterialEditForm
                          key={m.id}
                          initial={m}
                          vendors={vendors}
                          existingTags={allTags}
                          onCancel={() => setEditing(null)}
                          onSaved={async () => {
                            setEditing(null);
                            await load();
                          }}
                        />
                      ) : (
                        <MaterialRow
                          key={m.id}
                          material={m}
                          vendorById={vendorById}
                          showReorder={groupBy === 'tag'}
                          isFirst={idx === 0}
                          isLast={idx === group.items.length - 1}
                          onReorder={async (dir) => {
                            const tag = group.untagged ? undefined : group.key;
                            await reorderMaterialInGroup(m.id, dir, tag);
                            await load();
                          }}
                          onEdit={() => setEditing(m.id)}
                          onChanged={load}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
  inactiveColorClass,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  /** Tailwind class string for the inactive-state pill — typically the
   *  category's signature color so the chips themselves are color-coded.
   *  Falls back to a neutral paper-on-line look when omitted (used by "All"). */
  inactiveColorClass?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
        active
          ? 'bg-orange text-white border border-orange'
          : inactiveColorClass
            ? `${inactiveColorClass} hover:opacity-80`
            : 'bg-paper text-text border border-line-dark hover:border-orange'
      }`}
    >
      {label}
    </button>
  );
}

function MaterialRow({
  material,
  vendorById,
  showReorder,
  isFirst,
  isLast,
  onReorder,
  onEdit,
  onChanged,
}: {
  material: Material;
  vendorById: Map<string, Vendor>;
  showReorder: boolean;
  isFirst: boolean;
  isLast: boolean;
  onReorder: (dir: 'up' | 'down') => Promise<void>;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isActive = material.active !== false;

  async function handleDelete() {
    if (
      !window.confirm(
        `Delete material "${material.name}"? (soft-delete — can be restored)`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await softDeleteMaterial(material.id, 'deleted from Settings');
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function toggleActive() {
    setBusy(true);
    setError(null);
    try {
      await updateMaterial(material.id, { active: !isActive });
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReorder(dir: 'up' | 'down') {
    setBusy(true);
    setError(null);
    try {
      await onReorder(dir);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Build the "$2.45/lb to Alro · 2026-04-22" line from lastPaid. Vendor name
  // is looked up from the map; date formatted to YYYY-MM-DD for compactness.
  const lastPaidLine = useMemo(() => {
    const lp = material.lastPaid;
    if (!lp) return null;
    const vendorName = lp.vendorId ? vendorById.get(lp.vendorId)?.name : undefined;
    const datePart = lp.date ? lp.date.slice(0, 10) : '';
    const priceLabel = `$${lp.price}${PRICE_UNIT_LABELS[lp.unit].replace('$', '')}`;
    const vendorLabel = vendorName ? ` to ${vendorName}` : '';
    const dateLabel = datePart ? ` · ${datePart}` : '';
    return `${priceLabel}${vendorLabel}${dateLabel}`;
  }, [material.lastPaid, vendorById]);

  return (
    <div className={`bg-paper border border-line rounded p-3 ${isActive ? '' : 'opacity-60'}`}>
      <div className="flex items-start gap-3">
        {showReorder && (
          <ReorderButtons
            onUp={() => void handleReorder('up')}
            onDown={() => void handleReorder('down')}
            disabledUp={isFirst || busy}
            disabledDown={isLast || busy}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-semibold text-navy">{material.name}</div>
            <span
              className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${MATERIAL_CATEGORY_COLORS[material.category]}`}
            >
              {MATERIAL_CATEGORY_LABELS[material.category]}
            </span>
            {material.form && (
              <span className="text-[11px] text-text-mute">
                {MATERIAL_FORM_LABELS[material.form]}
              </span>
            )}
            {material.spec && (
              <span className="text-[11px] text-text-mute font-mono">{material.spec}</span>
            )}
            {!isActive && (
              <span className="text-[10px] uppercase tracking-wide text-text-mute font-mono">
                inactive
              </span>
            )}
            {material.importedFromV1 && (
              <span className="text-[10px] uppercase tracking-wide text-text-mute font-mono">
                imported
              </span>
            )}
          </div>

          {/* Tag pills (Justin's freeform tags) */}
          {material.tags && material.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {material.tags.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 bg-orange/10 border border-orange/30 rounded text-[11px] text-orange font-semibold"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Vendor pills */}
          {material.vendorIds && material.vendorIds.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {material.vendorIds.map((vid) => {
                const v = vendorById.get(vid);
                return (
                  <span
                    key={vid}
                    className="px-1.5 py-0.5 bg-bg border border-line rounded text-[11px] text-text"
                  >
                    {v ? v.name : 'unknown vendor'}
                  </span>
                );
              })}
            </div>
          )}

          {lastPaidLine && (
            <div className="text-xs text-text-mute mt-1 font-mono">{lastPaidLine}</div>
          )}

          {typeof material.density === 'number' && material.density > 0 && (
            <div className="text-[11px] text-text-mute mt-0.5">
              density {material.density} lb/in³
            </div>
          )}

          {error && <div className="text-xs text-orange mt-1">⚠ {error}</div>}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Toggle
            granted={isActive}
            onToggle={toggleActive}
            label={isActive ? 'Mark inactive' : 'Mark active'}
          />
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            className="px-2 py-1 text-xs font-semibold text-navy border border-line-dark rounded hover:bg-bg disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className="px-2 py-1 text-xs font-semibold text-orange border border-orange/40 rounded hover:bg-orange/10 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Form used both for "Add new material" and "Edit existing material". Pass
 * `initial=null` to create; pass a Material record to edit. `vendors` is the
 * full vendor list so the picker can render names + service hints.
 */
function MaterialEditForm({
  initial,
  vendors,
  existingTags,
  onCancel,
  onSaved,
}: {
  initial: Material | null;
  vendors: Vendor[];
  existingTags: string[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [category, setCategory] = useState<MaterialCategory>(initial?.category ?? 'aluminum');
  const [form, setForm] = useState<MaterialForm | ''>(initial?.form ?? '');
  const [spec, setSpec] = useState(initial?.spec ?? '');
  const [densityRaw, setDensityRaw] = useState(
    initial?.density != null ? String(initial.density) : '',
  );
  const [vendorIds, setVendorIds] = useState<string[]>(initial?.vendorIds ?? []);
  // Last-paid sub-form fields. We track them individually as strings so the
  // user can type partial values; coalesced into a MaterialPriceSnapshot at
  // save time. An empty price means "no lastPaid set" — we drop it.
  const [lpPriceRaw, setLpPriceRaw] = useState(
    initial?.lastPaid?.price != null ? String(initial.lastPaid.price) : '',
  );
  const [lpUnit, setLpUnit] = useState<MaterialPriceSnapshot['unit']>(
    initial?.lastPaid?.unit ?? 'per_lb',
  );
  const [lpVendorId, setLpVendorId] = useState(initial?.lastPaid?.vendorId ?? '');
  const [lpDate, setLpDate] = useState(
    initial?.lastPaid?.date ? initial.lastPaid.date.slice(0, 10) : '',
  );
  const [lpReference, setLpReference] = useState(initial?.lastPaid?.reference ?? '');
  const [active, setActive] = useState<boolean>(initial?.active !== false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [status, setStatus] = useState<SaveStatus>('idle');

  // Vendors sorted alpha for the multi-select. Favorites first, same as the
  // Vendors tab — Justin's preferred shops surface top.
  const vendorOptions = useMemo(() => {
    return [...vendors].sort((a, b) => {
      if ((a.favorite ? 1 : 0) !== (b.favorite ? 1 : 0)) {
        return (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0);
      }
      return a.name.localeCompare(b.name);
    });
  }, [vendors]);

  function toggleVendor(vid: string) {
    setVendorIds((prev) =>
      prev.includes(vid) ? prev.filter((x) => x !== vid) : [...prev, vid],
    );
  }

  function parseOptionalNumber(
    raw: string,
    label: string,
    opts: { min?: number; max?: number } = {},
  ): { ok: true; value: number | undefined } | { ok: false; error: string } {
    if (raw.trim() === '') return { ok: true, value: undefined };
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) return { ok: false, error: `${label} must be a number.` };
    if (opts.min != null && n < opts.min) {
      return { ok: false, error: `${label} must be ≥ ${opts.min}.` };
    }
    if (opts.max != null && n > opts.max) {
      return { ok: false, error: `${label} must be ≤ ${opts.max}.` };
    }
    return { ok: true, value: n };
  }

  async function handleSave() {
    setStatus('saving');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setStatus({ error: 'Material name is required.' });
      return;
    }

    const density = parseOptionalNumber(densityRaw, 'Density', { min: 0 });
    if (!density.ok) {
      setStatus({ error: density.error });
      return;
    }

    // Last-paid: if price is present, build the snapshot; otherwise drop it.
    let lastPaid: MaterialPriceSnapshot | undefined;
    if (lpPriceRaw.trim() !== '') {
      const lpPrice = parseOptionalNumber(lpPriceRaw, 'Last-paid price', { min: 0 });
      if (!lpPrice.ok) {
        setStatus({ error: lpPrice.error });
        return;
      }
      if (lpPrice.value == null) {
        setStatus({ error: 'Last-paid price must be a number.' });
        return;
      }
      lastPaid = {
        price: lpPrice.value,
        unit: lpUnit,
        vendorId: lpVendorId.trim() || undefined,
        date: lpDate || new Date().toISOString().slice(0, 10),
        reference: lpReference.trim() || undefined,
      };
    }

    const payload: CreateMaterialInput | UpdateMaterialInput = {
      name: trimmedName,
      tags: tags.length > 0 ? tags : undefined,
      category,
      form: form === '' ? undefined : form,
      spec: spec.trim() || undefined,
      density: density.value,
      vendorIds,
      lastPaid,
      notes: notes.trim() || undefined,
      active,
    };

    try {
      if (initial) {
        await updateMaterial(initial.id, payload as UpdateMaterialInput);
      } else {
        await createMaterial(payload as CreateMaterialInput);
      }
      await onSaved();
    } catch (e) {
      setStatus({ error: `Failed to save: ${(e as Error).message}` });
    }
  }

  return (
    <div className="bg-paper border-2 border-orange/40 rounded p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-navy">
          {initial ? `Edit ${initial.name}` : 'Add material'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-mute hover:text-orange"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 6061-T6 Aluminum"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
        <Field
          label="Tags"
          help="Drives grouping in this Settings list (first tag). Type and press Enter."
        >
          <TagInput
            value={tags}
            onChange={setTags}
            suggestions={existingTags}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as MaterialCategory)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-white focus:outline-none focus:border-orange"
          >
            {(Object.keys(MATERIAL_CATEGORY_LABELS) as MaterialCategory[]).map((c) => (
              <option key={c} value={c}>
                {MATERIAL_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Form" help="Bar, plate, sheet, etc. Leave blank if mixed.">
          <select
            value={form}
            onChange={(e) => setForm(e.target.value as MaterialForm | '')}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-white focus:outline-none focus:border-orange"
          >
            <option value="">— none —</option>
            {(Object.keys(MATERIAL_FORM_LABELS) as MaterialForm[]).map((f) => (
              <option key={f} value={f}>
                {MATERIAL_FORM_LABELS[f]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Spec" help="e.g. '1/4 plate', '24×48 sheet'. Free-text.">
          <input
            type="text"
            value={spec}
            onChange={(e) => setSpec(e.target.value)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Density (lb/in³)" help="For cost-by-weight calc. Optional.">
          <input
            type="number"
            min={0}
            step={0.001}
            value={densityRaw}
            onChange={(e) => setDensityRaw(e.target.value)}
            className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
      </div>

      <Field
        label="Vendors"
        help="Who supplies this material. Pick all that do — primary first."
      >
        {vendorOptions.length === 0 ? (
          <p className="text-xs text-text-mute italic">
            No vendors yet. Add vendors in the Vendors tab first.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {vendorOptions.map((v) => {
              const checked = vendorIds.includes(v.id);
              // Show top service-type as a hint so Justin can tell vendors apart
              // at a glance (Alro vs Metals USA vs phenolic supplier).
              const hint = v.services?.[0]
                ? ` · ${VENDOR_SERVICE_LABELS[v.services[0]]}`
                : '';
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => toggleVendor(v.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    checked
                      ? 'bg-orange text-white border-orange'
                      : 'bg-paper text-text border-line-dark hover:border-orange'
                  }`}
                >
                  {v.favorite ? '★ ' : ''}
                  {v.name}
                  <span className={`ml-1 font-normal ${checked ? 'text-white/80' : 'text-text-mute'}`}>
                    {hint}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Field>

      {/* Last-paid sub-form */}
      <div className="border border-line rounded p-3 space-y-3 bg-bg/50">
        <div className="text-xs font-semibold text-text-mute uppercase tracking-wide">
          Last paid (most recent price)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <Field label="Price">
            <input
              type="number"
              min={0}
              step={0.01}
              value={lpPriceRaw}
              onChange={(e) => setLpPriceRaw(e.target.value)}
              placeholder="2.45"
              className="w-full max-w-[140px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
          <Field label="Unit">
            <select
              value={lpUnit}
              onChange={(e) => setLpUnit(e.target.value as MaterialPriceSnapshot['unit'])}
              className="w-full max-w-[140px] px-2 py-1.5 border border-line-dark rounded text-sm bg-white focus:outline-none focus:border-orange"
            >
              {PRICE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {PRICE_UNIT_LABELS[u]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Vendor paid">
            <select
              value={lpVendorId}
              onChange={(e) => setLpVendorId(e.target.value)}
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-white focus:outline-none focus:border-orange"
            >
              <option value="">— none —</option>
              {vendorOptions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date paid">
            <input
              type="date"
              value={lpDate}
              onChange={(e) => setLpDate(e.target.value)}
              className="w-full max-w-[160px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>
        </div>
        <Field label="Reference (PO / receipt #)">
          <input
            type="text"
            value={lpReference}
            onChange={(e) => setLpReference(e.target.value)}
            placeholder="PO-12345"
            className="w-full max-w-md px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <p className="text-[11px] text-text-mute italic">
          Leave price blank to skip — last-paid is optional.
        </p>
      </div>

      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Finish, datasheet link, callout, etc."
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
        />
      </Field>

      <div className="flex items-center gap-2">
        <input
          id="material-active"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="accent-orange"
        />
        <label htmlFor="material-active" className="text-sm text-text">
          Active — show in material pickers
        </label>
      </div>

      <div className="pt-2 border-t border-line flex items-center gap-3">
        <SaveButton
          status={status}
          onClick={handleSave}
          label={initial ? 'Save changes' : 'Add material'}
        />
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-text-mute hover:text-orange"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Reusable form atoms
// =====================================================

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </label>
      {children}
      {help && <p className="text-[11px] text-text-mute mt-1 leading-snug">{help}</p>}
    </div>
  );
}

function NumberField({
  label,
  unit,
  step,
  help,
  value,
  onChange,
}: {
  label: string;
  unit: string;
  step: number;
  help?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          step={step}
          min={0}
          className="w-full max-w-[120px] px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
        />
        <span className="text-xs text-text-mute font-mono">{unit}</span>
      </div>
      {help && <p className="text-[11px] text-text-mute mt-1 leading-snug">{help}</p>}
    </div>
  );
}
